import { useState, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { fetchJson } from "../lib/api";
import { useUserStats, useHabits } from "../hooks/queries";
import { TerminalLine } from "../components/terminal/TerminalLine";
import { todayIST } from "../lib/time";
import { ContributionHeatmap } from "../components/terminal/ContributionHeatmap";
import { UserProfileCard } from "../components/terminal/UserProfileCard";
import { useTheme } from "../stores/useTheme";

export function StatsPage() {
  const [activeTab, setActiveTab] = useState<"stats" | "profile" | "rewards">("stats");
  const stats = useUserStats();
  const habits = useHabits();
  const queryClient = useQueryClient();
  const accent = useTheme((s) => s.accent);
  const setAccent = useTheme((s) => s.setAccent);
  const fg = useTheme((s) => s.fg);
  const fgDim = useTheme((s) => s.fgDim);
  const setFg = useTheme((s) => s.setFg);
  const setFgDim = useTheme((s) => s.setFgDim);
  const setThemePair = useTheme((s) => s.setThemePair);

  const monthlyXp = useQuery({
    queryKey: ["xp-monthly"],
    queryFn: () => fetchJson<Array<{ month: string; xp: number }>>("/api/stats/xp/monthly"),
  });

  const leaderboard = (habits.data ?? [])
    .filter((h) => h.is_active && (h.current_streak ?? 0) > 0)
    .sort((a, b) => (b.current_streak ?? 0) - (a.current_streak ?? 0))
    .slice(0, 5);

  // Profile setup states
  const [username, setUsername] = useState("");
  const [customQuote, setCustomQuote] = useState("");
  const [avatarAscii, setAvatarAscii] = useState("");
  const [saveStatus, setSaveStatus] = useState<string | null>(null);
  const [customTitle, setCustomTitle] = useState("");

  useEffect(() => {
    if (stats.data) {
      setUsername(stats.data.username ?? "shukan_user");
      setCustomQuote(stats.data.customQuote ?? "A clean space is a productive space.");
      setAvatarAscii(stats.data.avatarAscii ?? "");
      setCustomTitle(stats.data.customTitle ?? "Habit Seeker");
    }
  }, [stats.data]);

  const saveProfile = useMutation({
    mutationFn: async (vars: { username: string; customQuote: string; avatarAscii: string; customTitle: string }) => {
      return fetchJson("/api/user/stats", {
        method: "PATCH",
        body: JSON.stringify(vars),
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["user-stats"] });
      setSaveStatus("✓ profile saved successfully");
      setTimeout(() => setSaveStatus(null), 3000);
    },
    onError: (err) => {
      setSaveStatus(`✗ failed: ${err instanceof Error ? err.message : String(err)}`);
    }
  });

  const saveTheme = useMutation({
    mutationFn: async (nextAccent: string) => {
      return fetchJson("/api/user/stats", {
        method: "PATCH",
        body: JSON.stringify({ accent: nextAccent }),
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["user-stats"] });
    }
  });

  const applyAndSaveTheme = (hex: string) => {
    setAccent(hex);
    saveTheme.mutate(hex);
  };

  const avatarPresets = [
    { name: "Greenhorn", avatar: "  ( ^_^)\n  /|_|\\\n  /   \\" },
    { name: "Novice Planer", avatar: "  [  -_- ]\n  /|▓▓▓|\\\n  /   \\" },
    { name: "Aurora Seeker", avatar: "  \\('o')/\n  /|▓▓▓|\\\n  /   \\" },
    { name: "Mint Alchemist", avatar: "  (•‿•)\n  /|███|\\\n  /   \\" },
    { name: "Volcanic Druid", avatar: " (🔥_🔥)\n/|███|\\\n /   \\" },
    { name: "Cyber Warrior", avatar: "  (★_★)\n/|⚡▓⚡|\\\n /     \\" },
    { name: "Forest Ranger", avatar: "  (☘_☘)\n  /|▓▓▓|\\\n  /   \\" },
    { name: "Ocean Vanguard", avatar: "  (⚙_⚙)\n  /|☸▓☸|\\\n  /   \\" },
    { name: "Retro Runner", avatar: " (⚡_⚡)\n/|█▓█|\\\n /   \\" },
    { name: "Solar Knight", avatar: " (☀️_☀️)\n/|⚔️█⚔️|\\\n /     \\" },
    { name: "Cosmic Overlord", avatar: "  (☯_☯)\n/|⚛️█⚛️|\\\n /     \\" },
    { name: "Amethyst Mage", avatar: " (✨_✨)\n/|🔮█🔮|\\\n /     \\" },
    { name: "Dimension Shifter", avatar: " (👁️_👁️)\n/|🌀█🌀|\\\n /     \\" },
    { name: "Zen Master AGI", avatar: " (🌟_🌟)\n/|☯️█☯️|\\\n /     \\" }
  ];

  const themePresets = [
    "#f59e0b", "#22d3ee", "#34d399", "#f472b6", "#a78bfa", "#f87171",
    "#6366f1", "#14b8a6", "#f97316", "#3b82f6", "#84cc16", "#06b6d4",
    "#64748b", "#f43f5e", "#ef4444", "#eab308",
  ];

  const gradientPresets = [
    { id: "gradient-sunset", name: "Sunset Glow", level: 5, css: "linear-gradient(135deg, #f59e0b, #ef4444)" },
    { id: "gradient-aurora", name: "Aurora Borealis", level: 8, css: "linear-gradient(135deg, #0284c7, #10b981)" },
    { id: "gradient-mint", name: "Neon Mint", level: 10, css: "linear-gradient(135deg, #10b981, #06b6d4)" },
    { id: "gradient-volcano", name: "Volcanic Ash", level: 12, css: "linear-gradient(135deg, #ef4444, #7f1d1d)" },
    { id: "gradient-cyber", name: "Cyber Purple", level: 15, css: "linear-gradient(135deg, #8b5cf6, #ec4899)" },
    { id: "gradient-forest", name: "Forest Dew", level: 18, css: "linear-gradient(135deg, #059669, #84cc16)" },
    { id: "gradient-ocean", name: "Ocean Breeze", level: 20, css: "linear-gradient(135deg, #3b82f6, #22d3ee)" },
    { id: "gradient-vaporwave", name: "Retro Vapor", level: 22, css: "linear-gradient(135deg, #ff007f, #00f2fe)" },
    { id: "gradient-solar", name: "Solar Flare", level: 25, css: "linear-gradient(135deg, #f97316, #eab308)" },
    { id: "gradient-cosmic", name: "Cosmic Fire", level: 30, css: "linear-gradient(135deg, #f43f5e, #fb7185)" },
    { id: "gradient-amethyst", name: "Royal Amethyst", level: 35, css: "linear-gradient(135deg, #6366f1, #d946ef)" },
    { id: "gradient-midnight", name: "Midnight Cyber", level: 40, css: "linear-gradient(135deg, #be123c, #1e1b4b)" },
    { id: "gradient-zen", name: "Zen Master", level: 50, css: "linear-gradient(135deg, #4b5563, #1f2937)" },
  ];

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "1rem" }}>
      <TerminalLine prompt accent>stats & progression</TerminalLine>

      {/* Tabs */}
      <div style={{ display: "flex", gap: "0.5rem" }}>
        <button
          type="button"
          className={`shukan-btn ${activeTab === "stats" ? "shukan-btn-primary" : ""}`}
          onClick={() => setActiveTab("stats")}
        >
          [ Stats ]
        </button>
        <button
          type="button"
          className={`shukan-btn ${activeTab === "profile" ? "shukan-btn-primary" : ""}`}
          onClick={() => setActiveTab("profile")}
        >
          [ Profile ]
        </button>
        <button
          type="button"
          className={`shukan-btn ${activeTab === "rewards" ? "shukan-btn-primary" : ""}`}
          onClick={() => setActiveTab("rewards")}
        >
          [ Rewards ]
        </button>
      </div>

      {activeTab === "stats" && (
        <div style={{ display: "flex", flexDirection: "column", gap: "1rem" }}>
          {stats.data && (
            <section>
              <UserProfileCard stats={stats.data} />
            </section>
          )}

          <section>
            <ContributionHeatmap />
          </section>

          {/* SVG Bar Chart for Monthly XP */}
          <section style={{ border: "1px solid var(--border)", padding: "0.75rem", backgroundColor: "var(--bg-elevated)" }}>
            <TerminalLine>&gt; xp accumulation by month</TerminalLine>
            <div style={{ marginTop: "1rem" }}>
              <MonthlyXpChart data={monthlyXp.data ?? []} />
            </div>
          </section>

          {/* Streak Leaderboard */}
          <section style={{ border: "1px solid var(--border)", padding: "0.75rem", backgroundColor: "var(--bg-elevated)" }}>
            <TerminalLine>&gt; active streak leaderboard</TerminalLine>
            {leaderboard.length === 0 ? (
              <div style={{ fontSize: "0.8rem", color: "var(--fg-dim)", margin: "0.5rem 0 0 0" }}>
                $ no active streaks yet. complete habits daily to build streaks!
              </div>
            ) : (
              <div style={{ overflowX: "auto" }}>
                <table style={{ width: "100%", fontSize: "0.8rem", borderCollapse: "collapse", marginTop: "0.5rem", minWidth: "300px" }}>
                  <thead>
                    <tr style={{ borderBottom: "1px dashed var(--border)", textAlign: "left", color: "var(--fg-dim)" }}>
                      <th style={{ padding: "0.25rem 0" }}>habit</th>
                      <th style={{ padding: "0.25rem 0" }}>group</th>
                      <th style={{ padding: "0.25rem 0", textAlign: "right" }}>current streak</th>
                      <th style={{ padding: "0.25rem 0", textAlign: "right" }}>longest streak</th>
                    </tr>
                  </thead>
                  <tbody>
                    {leaderboard.map((h, idx) => (
                      <tr key={h.id} style={{ borderBottom: "1px solid var(--border)", opacity: 1 - idx * 0.1 }}>
                        <td style={{ padding: "0.4rem 0", display: "flex", alignItems: "center", gap: "0.4rem" }}>
                          <span>{h.icon}</span>
                          <strong>{h.name}</strong>
                        </td>
                        <td style={{ padding: "0.4rem 0" }}>
                          <span style={{ color: h.group_color }}>[{h.group_name}]</span>
                        </td>
                        <td style={{ padding: "0.4rem 0", color: "var(--accent)", textAlign: "right" }}>
                          🔥 {h.current_streak} days
                        </td>
                        <td style={{ padding: "0.4rem 0", color: "var(--fg-dim)", textAlign: "right" }}>
                          ⚡ {h.longest_streak} days
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </section>

          <section style={{ border: "1px solid var(--border)", padding: "0.75rem", backgroundColor: "var(--bg-elevated)", fontSize: "0.75rem", color: "var(--fg-dim)" }}>
            <span>$ system diagnostics: days left in year = {stats.data?.daysRemainingInYear ?? 0} · local time = {todayIST()}</span>
          </section>
        </div>
      )}

      {activeTab === "profile" && (
        <div
          className="shukan-card"
          style={{
            border: "1px solid var(--border)",
            backgroundColor: "var(--bg-elevated)",
            padding: "1.25rem",
            display: "flex",
            flexDirection: "column",
            gap: "1rem"
          }}
        >
          {stats.data && (
            <UserProfileCard stats={{ ...stats.data, username, customQuote, avatarAscii, customTitle }} />
          )}

          <TerminalLine prompt>&gt; edit profile details</TerminalLine>

          <div style={{ display: "flex", flexDirection: "column", gap: "0.25rem" }}>
            <label style={{ fontSize: "0.75rem", color: "var(--fg-dim)" }}>$ username</label>
            <input
              className="shukan-input"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              placeholder="username"
            />
          </div>

          <div style={{ display: "flex", flexDirection: "column", gap: "0.25rem" }}>
            <label style={{ fontSize: "0.75rem", color: "var(--fg-dim)" }}>$ custom motto / daily quote</label>
            <input
              className="shukan-input"
              value={customQuote}
              onChange={(e) => setCustomQuote(e.target.value)}
              placeholder="Motto..."
            />
          </div>

          <div style={{ display: "flex", flexDirection: "column", gap: "0.25rem" }}>
            <label style={{ fontSize: "0.75rem", color: "var(--fg-dim)" }}>$ select active title</label>
            <select
              className="shukan-input"
              value={customTitle}
              onChange={(e) => setCustomTitle(e.target.value)}
              style={{ fontSize: "0.75rem", fontFamily: "monospace", padding: "0.3rem" }}
            >
              {[
                { level: 1, name: "Greenhorn" },
                { level: 5, name: "Sunset Seeker" },
                { level: 8, name: "Aurora Seeker" },
                { level: 10, name: "Mint Alchemist" },
                { level: 12, name: "Volcanic Druid" },
                { level: 15, name: "Cyber Warrior" },
                { level: 18, name: "Forest Ranger" },
                { level: 20, name: "Ocean Vanguard" },
                { level: 22, name: "Retro Runner" },
                { level: 25, name: "Solar Knight" },
                { level: 30, name: "Cosmic Overlord" },
                { level: 35, name: "Amethyst Mage" },
                { level: 40, name: "Dimension Shifter" },
                { level: 50, name: "Zen Master AGI" },
              ].map((t) => {
                const userLevel = stats.data?.level ?? 0;
                const isUnlocked = userLevel >= t.level;
                return (
                  <option key={t.name} value={t.name} disabled={!isUnlocked}>
                    {t.name} {isUnlocked ? "" : `(Locked - Lvl ${t.level})`}
                  </option>
                );
              })}
            </select>
          </div>

          <div style={{ display: "flex", flexDirection: "column", gap: "0.25rem" }}>
            <label style={{ fontSize: "0.75rem", color: "var(--fg-dim)" }}>$ select ascii avatar preset</label>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(80px, 1fr))", gap: "0.5rem", marginTop: "0.25rem" }}>
              {avatarPresets.map((p, idx) => (
                <button
                  key={idx}
                  type="button"
                  className="shukan-btn"
                  style={{
                    height: "6.5rem",
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "space-between",
                    padding: "0.4rem",
                    borderColor: avatarAscii.trim() === p.avatar.trim() ? "var(--accent)" : "var(--border)",
                  }}
                  onClick={() => setAvatarAscii(p.avatar)}
                >
                  <span style={{ fontSize: "0.6rem", color: "var(--fg-dim)" }}>{p.name}</span>
                  <pre style={{ 
                    margin: 0, 
                    fontSize: "0.6rem", 
                    fontFamily: "'JetBrains Mono', monospace", 
                    whiteSpace: "pre", 
                    wordBreak: "keep-all", 
                    overflow: "visible", 
                    color: avatarAscii.trim() === p.avatar.trim() ? "var(--accent)" : "var(--fg)" 
                  }}>{p.avatar}</pre>
                </button>
              ))}
            </div>
          </div>

          <div style={{ display: "flex", flexDirection: "column", gap: "0.25rem" }}>
            <label style={{ fontSize: "0.75rem", color: "var(--fg-dim)" }}>$ custom ascii avatar (paste or edit)</label>
            <textarea
              className="shukan-input"
              style={{ minHeight: "6rem", fontFamily: "'JetBrains Mono', monospace", fontSize: "0.75rem" }}
              value={avatarAscii}
              onChange={(e) => setAvatarAscii(e.target.value)}
              placeholder="Paste custom ASCII art here..."
            />
          </div>

          <div style={{ display: "flex", gap: "0.5rem", alignItems: "center", marginTop: "0.25rem" }}>
            <button
              type="button"
              className="shukan-btn shukan-btn-primary"
              onClick={() => saveProfile.mutate({ username, customQuote, avatarAscii, customTitle })}
              disabled={saveProfile.isPending}
            >
              {saveProfile.isPending ? "$ saving..." : "$ save profile"}
            </button>
            {saveStatus && <span style={{ fontSize: "0.75rem", color: "var(--fg-dim)" }}>{saveStatus}</span>}
          </div>
        </div>
      )}

      {activeTab === "rewards" && (
        <div
          className="shukan-card"
          style={{
            border: "1px solid var(--border)",
            backgroundColor: "var(--bg-elevated)",
            padding: "1.25rem",
            display: "flex",
            flexDirection: "column",
            gap: "1.25rem"
          }}
        >
          <div>
            <TerminalLine prompt>&gt; theme rewards & accents</TerminalLine>
            <p style={{ fontSize: "0.8rem", color: "var(--fg-dim)", margin: "0.25rem 0 0 0" }}>
              Unlock flat colors and premium gradient themes by reaching level milestones.
            </p>
          </div>

          {/* Regular Colors Preset Picker */}
          <div>
            <div style={{ fontSize: "0.75rem", color: "var(--accent)", fontWeight: "bold", marginBottom: "0.5rem" }}>
              &gt; Standard Accent Colors
            </div>
            <div style={{ display: "flex", gap: "0.5rem", flexWrap: "wrap" }}>
              {themePresets.map((c) => (
                <button
                  key={c}
                  type="button"
                  onClick={() => applyAndSaveTheme(c)}
                  aria-label={`accent ${c}`}
                  style={{
                    width: "1.8rem",
                    height: "1.8rem",
                    borderRadius: "2px",
                    background: c,
                    border: c === accent ? "2.5px solid var(--fg)" : "1px solid var(--border)",
                    cursor: "pointer",
                  }}
                />
              ))}
            </div>
          </div>

          {/* Premium Gradient Accent Colors Picker */}
          <div>
            <div style={{ fontSize: "0.75rem", color: "var(--accent)", fontWeight: "bold", marginBottom: "0.5rem" }}>
              &gt; Unlocked Premium Gradients
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(130px, 1fr))", gap: "0.75rem" }}>
              {gradientPresets.map((g) => {
                const currentLevel = stats.data?.level ?? 0;
                const isUnlocked = currentLevel >= g.level;
                const isSelected = accent === g.id;

                return (
                  <button
                    key={g.id}
                    type="button"
                    disabled={!isUnlocked}
                    onClick={() => applyAndSaveTheme(g.id)}
                    style={{
                      height: "3rem",
                      borderRadius: "4px",
                      background: isUnlocked ? g.css : "rgba(255,255,255,0.03)",
                      border: isSelected
                        ? "2.5px solid var(--fg)"
                        : isUnlocked
                        ? "1px solid var(--border)"
                        : "1px dashed rgba(255,255,255,0.15)",
                      color: isUnlocked ? "#ffffff" : "var(--fg-dim)",
                      fontSize: "0.75rem",
                      fontWeight: "bold",
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      justifyContent: "center",
                      cursor: isUnlocked ? "pointer" : "not-allowed",
                      position: "relative",
                      boxShadow: isSelected ? "0 0 10px var(--accent-glow)" : "none",
                      textShadow: isUnlocked ? "0 1px 2px rgba(0, 0, 0, 0.8)" : "none",
                    }}
                  >
                    <div>{g.name}</div>
                    <div style={{ fontSize: "0.6rem", opacity: 0.8 }}>
                      {isUnlocked ? "Unlocked" : `Lvl ${g.level} Locked`}
                    </div>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Font Colors Section */}
          <div style={{ borderTop: "1px dashed var(--border)", paddingTop: "1rem", marginTop: "0.5rem" }}>
            <div style={{ fontSize: "0.75rem", color: "var(--fg-dim)", fontWeight: "bold", marginBottom: "0.5rem" }}>
              &gt; Font Color Customization
            </div>
            
            <div style={{ display: "flex", gap: "1rem", flexWrap: "wrap", marginBottom: "0.75rem" }}>
              <div style={{ display: "flex", flexDirection: "column", gap: "0.25rem" }}>
                <label style={{ fontSize: "0.7rem", color: "var(--fg-dim)" }}>$ primary font color</label>
                <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
                  <input
                    type="color"
                    value={fg}
                    onChange={(e) => setFg(e.target.value)}
                    style={{ width: "2rem", height: "2.1rem", background: "transparent", border: "1px solid var(--border)", cursor: "pointer" }}
                  />
                  <input
                    className="shukan-input"
                    style={{ maxWidth: "7rem", fontSize: "0.75rem" }}
                    value={fg}
                    onChange={(e) => setFg(e.target.value)}
                  />
                </div>
              </div>

              <div style={{ display: "flex", flexDirection: "column", gap: "0.25rem" }}>
                <label style={{ fontSize: "0.7rem", color: "var(--fg-dim)" }}>$ secondary font color</label>
                <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
                  <input
                    type="color"
                    value={fgDim}
                    onChange={(e) => setFgDim(e.target.value)}
                    style={{ width: "2rem", height: "2.1rem", background: "transparent", border: "1px solid var(--border)", cursor: "pointer" }}
                  />
                  <input
                    className="shukan-input"
                    style={{ maxWidth: "7rem", fontSize: "0.75rem" }}
                    value={fgDim}
                    onChange={(e) => setFgDim(e.target.value)}
                  />
                </div>
              </div>
            </div>

            {/* Font Pairing Templates */}
            <div style={{ fontSize: "0.7rem", color: "var(--fg-dim)", marginBottom: "0.5rem" }}>
              &gt; Predefined Font Templates
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(135px, 1fr))", gap: "0.5rem" }}>
              {[
                { name: "Default Gray", fg: "#e5e5e5", fgDim: "#8a8a8a" },
                { name: "Pure Terminal", fg: "#39ff14", fgDim: "#047857" },
                { name: "Cyber Sunset", fg: "#ff007f", fgDim: "#7c3aed" },
                { name: "Retro Amber", fg: "#fef08a", fgDim: "#ca8a04" },
                { name: "Arctic Ice", fg: "#ecfeff", fgDim: "#0891b2" },
                { name: "Classic Monochrome", fg: "#ffffff", fgDim: "#6b7280" },
              ].map((t, idx) => {
                const isSelected = fg.toLowerCase() === t.fg.toLowerCase() && fgDim.toLowerCase() === t.fgDim.toLowerCase();
                return (
                  <button
                    key={idx}
                    type="button"
                    className="shukan-btn"
                    onClick={() => setThemePair(t.fg, t.fgDim)}
                    style={{
                      padding: "0.4rem",
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      justifyContent: "center",
                      borderColor: isSelected ? "var(--accent)" : "var(--border)",
                      background: "var(--bg-elevated)",
                    }}
                  >
                    <span style={{ fontSize: "0.75rem", fontWeight: "bold" }}>{t.name}</span>
                    <div style={{ display: "flex", gap: "0.3rem", marginTop: "0.25rem" }}>
                      <span style={{ width: "0.6rem", height: "0.6rem", background: t.fg, border: "1px solid var(--border)", borderRadius: "1px" }} title="Primary font color" />
                      <span style={{ width: "0.6rem", height: "0.6rem", background: t.fgDim, border: "1px solid var(--border)", borderRadius: "1px" }} title="Secondary font color" />
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function MonthlyXpChart({ data }: { data: Array<{ month: string; xp: number }> }) {
  if (!data || data.length === 0) {
    return <div style={{ fontSize: "0.8rem", color: "var(--fg-dim)" }}>$ no monthly xp data available.</div>;
  }

  const maxVal = Math.max(...data.map((d) => d.xp), 100);
  const chartHeight = 110;
  const chartWidth = 460;
  const paddingLeft = 32;
  const paddingTop = 20;

  return (
    <svg width="100%" height="160" viewBox="0 0 500 160" style={{ fontFamily: "JetBrains Mono, monospace" }}>
      {/* Grid Lines */}
      {[0, 0.25, 0.5, 0.75, 1].map((ratio, idx) => {
        const y = paddingTop + chartHeight * (1 - ratio);
        const val = Math.round(maxVal * ratio);
        return (
          <g key={idx}>
            <line
              x1={paddingLeft}
              y1={y}
              x2={paddingLeft + chartWidth}
              y2={y}
              stroke="var(--border)"
              strokeDasharray="2 4"
            />
            <text
              x={paddingLeft - 8}
              y={y + 3}
              fill="var(--fg-dim)"
              fontSize="8"
              textAnchor="end"
            >
              {val}
            </text>
          </g>
        );
      })}

      {/* Bars */}
      {data.map((item, idx) => {
        const barWidth = Math.max(12, Math.floor(chartWidth / data.length) - 16);
        const gap = Math.floor(chartWidth / data.length);
        const x = paddingLeft + idx * gap + (gap - barWidth) / 2;
        const barHeight = (item.xp / maxVal) * chartHeight;
        const y = paddingTop + chartHeight - barHeight;

        // format Month name from "2026-03" to "Mar"
        const monthLabel = (() => {
          try {
            const parts = item.month.split("-");
            const d = new Date(Date.UTC(parseInt(parts[0] || "0"), parseInt(parts[1] || "1") - 1, 1));
            return d.toLocaleString("default", { month: "short", timeZone: "UTC" });
          } catch {
            return item.month;
          }
        })();

        return (
          <g key={idx}>
            {/* Bar */}
            <rect
              x={x}
              y={y}
              width={barWidth}
              height={barHeight}
              fill="var(--accent)"
              opacity="0.8"
              rx="2"
              style={{ transition: "opacity 0.2s, fill 0.2s", cursor: "pointer" }}
            >
              <title>{`${item.month}: ${item.xp} XP`}</title>
            </rect>
            {/* Value Label above Bar */}
            {item.xp > 0 && (
              <text
                x={x + barWidth / 2}
                y={y - 5}
                fill="var(--fg)"
                fontSize="8"
                textAnchor="middle"
              >
                {item.xp}
              </text>
            )}
            {/* X Axis Label */}
            <text
              x={x + barWidth / 2}
              y={paddingTop + chartHeight + 14}
              fill="var(--fg-dim)"
              fontSize="8"
              textAnchor="middle"
            >
              {monthLabel}
            </text>
          </g>
        );
      })}

      {/* Bottom Axis Line */}
      <line
        x1={paddingLeft}
        y1={paddingTop + chartHeight}
        x2={paddingLeft + chartWidth}
        y2={paddingTop + chartHeight}
        stroke="var(--border)"
      />
    </svg>
  );
}
